import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronDown, User } from 'lucide-react';

import Button from '../ui/Button';
import { useAuth } from '../../context/AuthContext';
import { useAuthUI } from '../../context/AuthUIContext';
import { useSystemInfo } from '../../context/SystemContext';
import { useToast } from '../ui/Toast';

// Renders the header auth controls. variant: 'desktop' | 'mobile'.
// - guest: Login/Register buttons, each gated by system permissions
// - user: greeting + Orders / Admin (if admin) / Logout
// Modal opening is delegated to the global AuthUIContext.
export default function AuthWidget({ variant, onNavigate }) {
  const { user, isAdmin, logout } = useAuth();
  const { openLogin, openRegister } = useAuthUI();
  const { showLogin, showRegistration } = useSystemInfo();
  const { addToast } = useToast();
  const navigate = useNavigate();

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Close the desktop dropdown on outside click / Escape.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDocClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const afterNav = () => {
    setMenuOpen(false);
    onNavigate?.();
  };

  const handleLogout = async () => {
    await logout();
    setMenuOpen(false);
    onNavigate?.();
    addToast('Signed out.');
    navigate('/');
  };

  // ---- guest ----
  if (!user) {
    return (
      <div style={{ display: 'flex', gap: 'var(--ss-space-2)' }}>
        {showLogin && (
          <Button variant="ghost" size="sm" onClick={openLogin}>
            Log in
          </Button>
        )}
        {showRegistration && (
          <Button variant="primary" size="sm" onClick={openRegister}>
            Register
          </Button>
        )}
      </div>
    );
  }

  // ---- authenticated (mobile: inline list) ----
  if (variant === 'mobile') {
    return (
      <div className="ss-authmobile">
        <span className="ss-authmobile__greeting">Hi, {user.name}</span>
        <Link to="/orders" onClick={afterNav}>Orders</Link>
        {isAdmin && <Link to="/admin" onClick={afterNav}>Admin</Link>}
        <Link to="/profile" onClick={afterNav}>Profile</Link>
        <Button variant="ghost" size="sm" onClick={handleLogout}>Log out</Button>
      </div>
    );
  }

  // ---- authenticated (desktop: dropdown) ----
  return (
    <div className="ss-usermenu" ref={menuRef}>
      <button
        type="button"
        className="ss-usermenu__button"
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        onClick={() => setMenuOpen((o) => !o)}
      >
        <User size={18} aria-hidden="true" />
        <span>Hi, {user.name}</span>
        <ChevronDown size={16} aria-hidden="true" />
      </button>
      {menuOpen && (
        <div className="ss-usermenu__panel" role="menu">
          <Link to="/orders" role="menuitem" onClick={afterNav}>Orders</Link>
          {isAdmin && <Link to="/admin" role="menuitem" onClick={afterNav}>Admin</Link>}
          <Link to="/profile" role="menuitem" onClick={afterNav}>Profile</Link>
          <button type="button" role="menuitem" onClick={handleLogout}>Log out</button>
        </div>
      )}
    </div>
  );
}
